import { ContentfulDocumentSchema } from '@ravago/shared/page-data/validators/contentful-document.validator';

import { z } from 'zod';

import { TypeBrand } from '../models/contentful-generated';
import { BrandSubgroupEntrySchema } from './brand-subgroup.validator';
import { ContentfulAssetSchema } from './contentful-asset.validator';
import { ContentfulSysSchema } from './contentful-sys.validator';
import { ProducerEntrySchema } from './producer.validator';
import { SeoMetadataEntrySchema } from './seo-metadata.validator';

export const BrandEntrySchema = z.object({
  sys: ContentfulSysSchema,
  fields: z.looseObject({
    name: z.string(),
    slug: z.string(),
    producer: ProducerEntrySchema,
    countries: z.array(z.any()).optional().nullable(),
    seoMetadata: SeoMetadataEntrySchema.optional().nullable(),
    brandSubgroup: z.array(BrandSubgroupEntrySchema),
    brandDescription: z.union([z.string(), ContentfulDocumentSchema]).optional().nullable(), // Backward compatible: accepts both string (old) and RichText (new)
    brandBannerImage: ContentfulAssetSchema.optional().nullable(),
    excerpt: z.string().optional().nullable()
  }),
  metadata: z
    .object({
      tags: z.array(z.any()).optional().nullable()
    })
    .optional()
    .nullable()
});

export function validateBrandEntry(data: TypeBrand) {
  return BrandEntrySchema.safeParse(data);
}

/**
 * Returns validation result.
 *
 * @param data
 */
export function validateBrandEntries(data: TypeBrand[]) {
  return z.array(BrandEntrySchema).safeParse(data);
}

/**
 * Returns valid entries only. In most cases, this is preferred,
 * It allows us to still show some entries instead of showing nothing when only a single entry is invalid.
 *
 * @param data
 */
export function validateAndFilterBrandEntries(data: TypeBrand[]) {
  if (!data) return [];

  return data.reduce((acc: TypeBrand[], entry) => {
    const validationResult = validateBrandEntry(entry);
    if (validationResult.success) {
      acc.push(validationResult.data as unknown as TypeBrand);
    } else {
      console.warn(
        `Validation failed for brand entry with id: '${entry?.sys?.id}'. It will be filtered out. Error: ${validationResult.error.message}`
      );
    }
    return acc;
  }, []);
}
